import { useState } from 'react';
import AnimateHeight from 'react-animate-height';
import { useTranslation } from 'react-i18next';
import { FiArrowDownRight, FiArrowUpRight } from 'react-icons/fi';

import * as S from './CollapsibleField.styles';

/** Slot content, or a render function that receives the current expanded state. */
type Slot = React.ReactNode | ((isOpen: boolean) => React.ReactNode);

type CollapsibleFieldProps = Readonly<{
  /** Code column (or code + name columns when `name` is not given). */
  field: Slot;
  /** Name column next to the field. */
  name?: Slot;
  /** Revealed below the row when expanded; mounted on first open. */
  details?: React.ReactNode;
  defaultOpen?: boolean;
  disabled?: boolean;
  /** Arrow on the label line instead of next to the field box (e.g. a label-only collapsed row). */
  toggleOnLabel?: boolean | ((isOpen: boolean) => boolean);
  'data-testid'?: string;
}>;

const renderSlot = (slot: Slot, isOpen: boolean) =>
  typeof slot === 'function' ? slot(isOpen) : slot;

export function CollapsibleField({
  field,
  name,
  details,
  defaultOpen = false,
  disabled = false,
  toggleOnLabel = false,
  'data-testid': testId,
}: CollapsibleFieldProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(defaultOpen);
  // Once revealed, keep details mounted so they don't unmount/remount on toggle.
  const [hasBeenOpened, setHasBeenOpened] = useState(defaultOpen);

  const handleToggle = () => {
    setIsOpen((prev) => {
      const next = !prev;
      if (next && !hasBeenOpened) {
        setHasBeenOpened(true);
      }
      return next;
    });
  };

  const toggleLabel = isOpen ? t('common.hideDetails') : t('common.showDetails');
  const hasName = name !== undefined;
  const onLabel = typeof toggleOnLabel === 'function' ? toggleOnLabel(isOpen) : toggleOnLabel;

  return (
    <S.Wrapper data-testid={testId}>
      <S.FieldRow>
        <S.ToggleButton
          type="button"
          aria-expanded={isOpen}
          aria-label={toggleLabel}
          disabled={disabled}
          $onLabel={onLabel}
          onClick={handleToggle}
          data-testid={testId ? `${testId}-toggle` : 'collapsible-field-toggle'}
        >
          {isOpen ? <FiArrowUpRight /> : <FiArrowDownRight />}
        </S.ToggleButton>
        <S.FieldContent $wide={!hasName}>{renderSlot(field, isOpen)}</S.FieldContent>
        {hasName ? <S.NameContent>{renderSlot(name, isOpen)}</S.NameContent> : null}
      </S.FieldRow>

      {details !== undefined ? (
        <AnimateHeight duration={300} height={isOpen ? 'auto' : 0}>
          <S.DetailInset>{hasBeenOpened ? details : null}</S.DetailInset>
        </AnimateHeight>
      ) : null}
    </S.Wrapper>
  );
}
